import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { StepComponentProps } from "../../components/MultiStepGradeModal";
import { InfoModal } from "../../../../../../core/modal/InfoModal";
import { RubricGeneratorCreateRequestDto } from "@packages/api/madlenAPI.schemas";

export const CriteriaAndPointStep: React.FC<
  StepComponentProps<RubricGeneratorCreateRequestDto>
> = ({ data, onDataChange, themeColor }) => {
  const { t } = useTranslation();
  const [showInfoModal, setShowInfoModal] = useState(false);

  // Define criteria categories and their options
  const criteriaCategories = [
    {
      id: "content",
      title: t("rubric.criteria.content.title", "İçerik Kriterleri"),
      description: t(
        "rubric.criteria.content.description",
        "Bilgi, doğruluk, anlama derinliği, kanıt kullanımı",
      ),
      items: [
        {
          id: "knowledge",
          emoji: "🧠",
          label: t("rubric.criteria.knowledge", "Bilgi"),
        },
        {
          id: "accuracy",
          emoji: "✅",
          label: t("rubric.criteria.accuracy", "Doğruluk"),
        },
        {
          id: "depth",
          emoji: "🔍",
          label: t("rubric.criteria.depth", "Anlama Derinliği"),
        },
        {
          id: "evidence",
          emoji: "📊",
          label: t("rubric.criteria.evidence", "Kanıt Kullanımı"),
        },
      ],
    },
    {
      id: "technical",
      title: t("rubric.criteria.technical.title", "Teknik Kriterler"),
      description: t(
        "rubric.criteria.technical.description",
        "Gramer, biçimlendirme, atıflar, metodoloji, veri analizi",
      ),
      items: [
        {
          id: "grammar",
          emoji: "📝",
          label: t("rubric.criteria.grammar", "Gramer"),
        },
        {
          id: "formatting",
          emoji: "🎨",
          label: t("rubric.criteria.formatting", "Biçimlendirme"),
        },
        {
          id: "references",
          emoji: "📚",
          label: t("rubric.criteria.references", "Atıflar"),
        },
        {
          id: "methodology",
          emoji: "⚙️",
          label: t("rubric.criteria.methodology", "Metodoloji"),
        },
        {
          id: "data_analysis",
          emoji: "📈",
          label: t("rubric.criteria.dataAnalysis", "Veri Analizi"),
        },
      ],
    },
    {
      id: "process",
      title: t("rubric.criteria.process.title", "Süreç Kriterleri"),
      description: t(
        "rubric.criteria.process.description",
        "Organizasyon, zaman yönetimi, işbirliği, eleştirel düşünme",
      ),
      items: [
        {
          id: "organization",
          emoji: "📋",
          label: t("rubric.criteria.organization", "Organizasyon"),
        },
        {
          id: "time_management",
          emoji: "⏰",
          label: t("rubric.criteria.timeManagement", "Zaman Yönetimi"),
        },
        {
          id: "collaboration",
          emoji: "👥",
          label: t("rubric.criteria.collaboration", "İşbirliği"),
        },
        {
          id: "critical_thinking",
          emoji: "🤔",
          label: t("rubric.criteria.criticalThinking", "Eleştirel Düşünme"),
        },
      ],
    },
  ];

  // Handle criteria selection
  const handleCriteriaSelect = (criteriaId: string) => {
    const currentFocus = data.evaluationCriteria || [];
    const isSelected = currentFocus.includes(criteriaId);

    if (isSelected) {
      // Remove criteria
      onDataChange({
        evaluationCriteria: currentFocus.filter((id) => id !== criteriaId),
      });
    } else {
      // Add criteria
      onDataChange({
        evaluationCriteria: [...currentFocus, criteriaId],
      });
    }
  };

  // Handle total score change
  const handleTotalScoreChange = (score: number) => {
    onDataChange({ totalScore: score });
  };

  const infoModalExamples = [
    {
      emoji: "🎯",
      title: t(
        "rubric.criteria.info.title",
        "Öğretmen Rehberi: Kriter Seçimi İpuçları",
      ),
      description: t(
        "rubric.criteria.info.description",
        "Öğrenme hedeflerinizle uyumlu 3-6 kriter seçin. Çok fazla kriter öğrencileri karıştırabilir.",
      ),
    },
    {
      emoji: "📝",
      title: t("rubric.criteria.content.title", "İçerik Kriterleri"),
      description: t(
        "rubric.criteria.content.description",
        "Bilgi, doğruluk, anlama derinliği, kanıt kullanımı",
      ),
    },
    {
      emoji: "⚙️",
      title: t("rubric.criteria.technical.title", "Teknik Kriterler"),
      description: t(
        "rubric.criteria.technical.description",
        "Gramer, biçimlendirme, atıflar, metodoloji, veri analizi",
      ),
    },
    {
      emoji: "🔄",
      title: t("rubric.criteria.process.title", "Süreç Kriterleri"),
      description: t(
        "rubric.criteria.process.description",
        "Organizasyon, zaman yönetimi, işbirliği, eleştirel düşünme",
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2
          className={`text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-${themeColor}-600 to-${themeColor}-800 dark:from-${themeColor}-400 dark:to-${themeColor}-600`}
        >
          {t("rubric.steps.criteria.title", "Değerlendirme Kriterleri")}
        </h2>
        <div className="flex items-center justify-center gap-2 mt-2">
          <p className="text-gray-600 dark:text-gray-400">
            {t(
              "rubric.steps.criteria.description",
              "Ödevinizde en önemli olan şeyleri seçin",
            )}
          </p>
          <button
            onClick={() => setShowInfoModal(true)}
            className={`inline-flex items-center justify-center w-5 h-5 rounded-full bg-${themeColor}-100 dark:bg-${themeColor}-900/30 hover:bg-${themeColor}-200 dark:hover:bg-${themeColor}-900/50 transition-colors duration-200 group`}
            title={t(
              "rubric.info.criteriaGuide",
              "Kriter seçimi hakkında bilgi",
            )}
          >
            <svg
              className={`w-3 h-3 text-${themeColor}-600 dark:text-${themeColor}-400 group-hover:scale-110 transition-transform duration-200`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Important tip */}
      <div
        className={`bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4`}
      >
        <div className="flex items-start gap-3">
          <div className="text-red-500 mt-0.5">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div>
            <h3 className="font-medium text-red-800 dark:text-red-300">
              {t(
                "rubric.criteria.important.title",
                "Ödevinizde en önemli olan şeyleri seçin",
              )}
            </h3>
            <p className="text-sm text-red-700 dark:text-red-400 mt-1">
              {t(
                "rubric.criteria.important.description",
                "Bu ödev için en önemli olan 3-6 temel alanı seçin. Her kriter için tüm seviyelerde ayrıntılı performans açıklamaları oluşturulacaktır.",
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Info Modal */}
      <InfoModal
        isOpen={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        title={t(
          "rubric.info.criteriaGuide.title",
          "Öğretmen Rehberi: Kriter Seçimi",
        )}
        description={t(
          "rubric.info.criteriaGuide.description",
          "Öğrenme hedeflerinizle uyumlu 3-6 kriter seçin. Çok fazla kriter öğrencileri karıştırabilir.",
        )}
        examples={infoModalExamples}
        themeColor={themeColor}
        icon="🎯"
      />

      {/* Teacher's Guide */}
      <div
        className={`bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4`}
      >
        <div className="flex items-start gap-3">
          <div className="text-purple-600 dark:text-purple-400">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <h3 className="font-medium text-purple-800 dark:text-purple-300">
              {t(
                "rubric.criteria.teacherGuide.title",
                "Öğretmen Rehberi: Kriter Seçimi İpuçları",
              )}
            </h3>
            <div className="space-y-2 mt-2">
              {criteriaCategories.map((category) => (
                <div key={category.id} className="text-sm">
                  <span className="font-medium text-purple-700 dark:text-purple-300">
                    {category.title}:
                  </span>
                  <span className="text-purple-600 dark:text-purple-400 ml-2">
                    {category.description}
                  </span>
                </div>
              ))}
              <div className="text-sm pt-2 border-t border-purple-200 dark:border-purple-700">
                <span className="font-medium text-purple-700 dark:text-purple-300">
                  {t(
                    "rubric.criteria.professionalTip.title",
                    "Profesyonel İpucu:",
                  )}
                </span>
                <span className="text-purple-600 dark:text-purple-400 ml-2">
                  {t(
                    "rubric.criteria.professionalTip.description",
                    "Öğrenme hedeflerinizle uyumlu 3-6 kriter seçin. Çok fazla kriter öğrencileri karıştırabilir.",
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Criteria Selection */}
      <div className="space-y-6">
        {criteriaCategories.map((category) => (
          <div key={category.id} className="space-y-3">
            <h3
              className={`text-lg font-medium text-${themeColor}-700 dark:text-${themeColor}-300`}
            >
              {category.title}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {category.items.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleCriteriaSelect(item.id)}
                  className={`p-3 rounded-lg border-2 transition-all duration-200 text-center ${
                    data.evaluationCriteria?.includes(item.id)
                      ? `border-${themeColor}-500 bg-${themeColor}-50 dark:bg-${themeColor}-900/20`
                      : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
                  }`}
                >
                  <div className="text-2xl mb-1">{item.emoji}</div>
                  <div className="text-sm font-medium">{item.label}</div>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Total Score Slider */}
      <div className="mt-6">
        <div className="text-center mb-4">
          <h3
            className={`text-lg font-medium text-${themeColor}-700 dark:text-${themeColor}-300`}
          >
            {t("rubric.totalScore.title", "Toplam Puan")}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t(
              "rubric.totalScore.slider.description",
              "Kaydırarak toplam puanı ayarlayın",
            )}
          </p>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between mb-2 ml-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">0</span>
            <span className="text-sm text-gray-600 dark:text-gray-400 ml-1">
              25
            </span>
            <span className="text-sm text-gray-600 dark:text-gray-400">50</span>
            <span className="text-sm text-gray-600 dark:text-gray-400">75</span>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              100
            </span>
          </div>

          <div className="relative">
            <input
              type="range"
              min="0"
              max="100"
              value={data.totalScore || 100}
              onChange={(e) => {
                const value = parseInt(e.target.value);
                handleTotalScoreChange(value);
              }}
              className="w-full h-2 bg-gradient-to-r from-red-400 via-yellow-400 via-green-400 to-green-600 rounded-full appearance-none cursor-pointer slider"
              style={{
                background: `linear-gradient(to right, 
                  rgb(248 113 113) 0%, 
                  rgb(251 191 36) 25%, 
                  rgb(163 230 53) 50%, 
                  rgb(34 197 94) 75%, 
                  rgb(34 197 94) 100%)`,
              }}
            />
          </div>

          <div className="text-center">
            <span
              className={`inline-flex items-center justify-center w-12 h-12 rounded-full bg-${themeColor}-500 text-white font-bold text-lg shadow-lg`}
            >
              {data.totalScore || 100}
            </span>
          </div>
        </div>
      </div>

      <style>
        {`
          .slider::-webkit-slider-thumb {
            appearance: none;
            width: 20px;
            height: 20px;
            border-radius: 50%;
            background: white;
            border: 3px solid rgb(59 130 246);
            cursor: pointer;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
          }

          .slider::-moz-range-thumb {
            width: 20px;
            height: 20px;
            border-radius: 50%;
            background: white;
            border: 3px solid rgb(59 130 246);
            cursor: pointer;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
          }

          .slider::-webkit-slider-track {
            height: 8px;
            border-radius: 4px;
          }

          .slider::-moz-range-track {
            height: 8px;
            border-radius: 4px;
          }
        `}
      </style>
    </div>
  );
};
